import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import {
  isTrainServicePath,
  MEMBER_SERVICE_BASE_URL,
  TRAIN_SERVICE_BASE_URL,
} from './constants';
import { storageAuth } from './utils/storage';

// 错误处理方案： 错误类型
enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}
// 与后端约定的响应数据格式
interface ResponseStructure {
  success: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
}

function normalizeBizCode(code: unknown): number | undefined {
  if (typeof code === 'number' && !Number.isNaN(code)) {
    return code;
  }
  if (typeof code === 'string' && code.trim() !== '') {
    const n = Number(code);
    if (!Number.isNaN(n)) {
      return n;
    }
  }
  return undefined;
}

/** 后端统一包 { code, msg, data }：code !== 0 时全局提示 msg；返回是否已提示 */
function showBizErrorIfNeeded(data: unknown, skip?: boolean): boolean {
  if (skip || data == null) {
    return false;
  }
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    return false;
  }
  if (typeof data !== 'object' || Array.isArray(data)) {
    return false;
  }
  const body = data as Record<string, unknown>;
  const code = normalizeBizCode(body.code);
  if (code === undefined || code === 0) {
    return false;
  }
  const rawMsg = body.msg ?? body.message ?? body.errorMessage ?? '操作失败';
  message.error(String(rawMsg));
  return true;
}

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const requestConfig: RequestConfig = {
  /** 后端多在 JSON 体里用 code/msg 表示业务成败；允许 4xx/5xx 仍走响应拦截器与业务提示，避免仅弹「Response status:500」而丢失 msg */
  validateStatus: () => true,

  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const payload = res as unknown as Record<string, unknown> &
        Partial<ResponseStructure>;
      const bizCode = normalizeBizCode(payload.code);
      // 与后端 { code, msg } 约定一致：非 0 已在 response 拦截器提示，此处不再抛错，避免破坏调用方同步判断 res.code 的写法
      if (bizCode !== undefined && bizCode !== 0) {
        return;
      }
      const { success, data, errorCode, errorMessage, showType } =
        res as unknown as ResponseStructure;
      if (!success) {
        const error: any = new Error(errorMessage);
        error.name = 'BizError';
        error.info = { errorCode, errorMessage, showType, data };
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          const { errorMessage, errorCode } = errorInfo;
          switch (errorInfo.showType) {
            case ErrorShowType.SILENT:
              // do nothing
              break;
            case ErrorShowType.WARN_MESSAGE:
              message.warning(errorMessage);
              break;
            case ErrorShowType.ERROR_MESSAGE:
              message.error(errorMessage);
              break;
            case ErrorShowType.NOTIFICATION:
              notification.open({
                description: errorMessage,
                message: errorCode,
              });
              break;
            case ErrorShowType.REDIRECT:
              // TODO: redirect
              break;
            default:
              message.error(errorMessage);
          }
        }
      } else if (error.response) {
        // HTTP 非 2xx 时 Axios 走失败分支；若响应体仍为 { code, msg } 业务包，优先提示 msg
        const showedBiz = showBizErrorIfNeeded(error.response.data);
        if (!showedBiz) {
          message.error(`Response status:${error.response.status}`);
        }
      } else if (error.request) {
        // 请求已经成功发起，但没有收到响应
        // \`error.request\` 在浏览器中是 XMLHttpRequest 的实例，
        // 而在node.js中是 http.ClientRequest 的实例
        message.error('None response! Please retry.');
      } else {
        // 发送请求时出了点问题
        message.error('Request error, please retry.');
      }
    },
  },

  // 请求拦截器
  requestInterceptors: [
    async (url, options) => {
      const nextOptions = { ...options } as Record<string, any>;
      const explicitBase = nextOptions.baseURL as string | undefined;
      // 未显式指定其它服务（如 safety-service）时：培训路径走 train-service，其余默认 member-service
      const canRouteByPath =
        !explicitBase ||
        explicitBase === MEMBER_SERVICE_BASE_URL ||
        explicitBase.includes('/member-service');
      if (canRouteByPath && isTrainServicePath(url)) {
        nextOptions.baseURL = TRAIN_SERVICE_BASE_URL;
      }

      const isLoginRequest = url.includes('/login');
      if (!isLoginRequest) {
        const headers = {
          ...(nextOptions.headers || {}),
        } as Record<string, unknown>;
        const hasAuth =
          headers.Authorization != null || headers.authorization != null;
        // 调用方已显式传 Authorization（如安防大屏会话）时不覆盖
        if (!hasAuth) {
          const storageData = storageAuth.get();
          if (storageData?.tokenName && storageData?.tokenValue) {
            headers[storageData.tokenName as string] = storageData.tokenValue;
          }
        }
        return {
          url,
          options: {
            ...nextOptions,
            headers,
          },
        } as any;
      }
      return { url, options: nextOptions };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    async (response) => {
      const skip =
        (response.config as { skipBizErrorMessage?: boolean })
          ?.skipBizErrorMessage === true;
      showBizErrorIfNeeded(response.data, skip);
      return response;
    },
  ],
};
